import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import videoParser from "js-video-url-parser";
import { IProps } from "./interfaces";
import { InputText, Label, InputNumber } from "../../../../../Form";
import Icon from "../../../../../../icon";
import { getStartSeconds, getEndSeconds, aspectRatio } from "../../../../../../../common/parameters";
import { editTask } from "../../../../../../store/plan/actions";
import { post } from "../../../../../../actions/general";
import { IEmbedData } from "../../../../../../../common/types";

import {
    StyledInputs,
    LinkRow,
    ArrowWrapper,
    YoutubeInputWrapper,
    IframeWrapper,
    TimeRow,
    Time,
    Hour,
    Minute,
    Second,
    Dots,
    Container,
    RightSide,
} from "./styles";
import messages from "./messages";
import youtubeApi from "../../youtubeApi";

const VideoEditor = ({ taskIndex, plan, data, data: { startHour, startMinute, startSecond, endHour, endMinute, endSecond } }: IProps) => {
    const domId = useRef("player_" + Date.now()).current;
    const [url, setUrl] = useState("");
    const dispatch = useDispatch();
    const changeHref = (e: React.FormEvent<HTMLInputElement>) => {
        const _url = e.currentTarget.value;
        setUrl(_url);
        const result = videoParser.parse(_url)!;
        if (result && result.id) {
            dispatch(editTask({ taskIndex, changingProp: "href", val: result.id }));
            saveHref();
        }
    };
    const saveHref = () => {
        dispatch(post("embed-save-href", {
            planId: plan._id,
            index: taskIndex,
            href: (plan.tasks[taskIndex].data as IEmbedData).href
        }));
    };
    const player = useRef<any>(null);

    const setupVideo = () => {
        if (!data.href) return;
        if (!window.YT || !window.YT.Player || !document.getElementById(domId)) {
            setTimeout(setupVideo, 50);
            return;
        }
        if (player.current) {
            player.current.destroy();
        }
        player.current = new window.YT.Player(domId, {
            height: aspectRatio.height / 2,
            width: aspectRatio.width / 2,
            videoId: data.href,
            playerVars: {
                'playsinline': 1,
                start: getStartSeconds(data),
                end: getEndSeconds(data),
            },
            events: {
                // 'onReady': onPlayerReady,
                // 'onStateChange': onPlayerStateChange
            }
        });
    }
    useEffect(() => {
        youtubeApi();
        setTimeout(setupVideo, 50);
    }, [data.href, data.startHour, data.startMinute, data.startSecond, data.endHour, data.endMinute, data.endSecond]);

    const changeStartHour = (e: React.FormEvent<HTMLInputElement>) => {
        const startHour = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(9, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "startHour", val: startHour }));
        saveStartTime();
    };
    const changeStartMinute = (e: React.FormEvent<HTMLInputElement>) => {
        const startMinute = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(59, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "startMinute", val: startMinute }));
        saveStartTime();
    };
    const changeStartSecond = (e: React.FormEvent<HTMLInputElement>) => {
        const startSecond = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(59, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "startSecond", val: startSecond }));
        saveStartTime();
    };
    const saveStartTime = () => {
        const data = plan.tasks[taskIndex].data as IEmbedData;
        const { startSecond, startMinute, startHour } = data;
        dispatch(post("embed-save-start-time", { planId: plan._id, index: taskIndex, startSecond, startMinute, startHour }));
    };
    const changeEndHour = (e: React.FormEvent<HTMLInputElement>) => {
        const endHour = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(9, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "endHour", val: endHour }));
        saveEndTime();
    };
    const changeEndMinute = (e: React.FormEvent<HTMLInputElement>) => {
        const endMinute = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(59, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "endMinute", val: endMinute }));
        saveEndTime();
    };
    const changeEndSecond = (e: React.FormEvent<HTMLInputElement>) => {
        const endSecond = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(59, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "endSecond", val: endSecond }));
        saveEndTime();
    };
    const saveEndTime = () => {
        const data = plan.tasks[taskIndex].data as IEmbedData;
        const { endHour, endMinute, endSecond } = data;
        dispatch(post("embed-save-end-time", { planId: plan._id, index: taskIndex, endSecond, endMinute, endHour }));
    };
    return (
        <Container>
            <RightSide>
                <StyledInputs>
                    <LinkRow>
                        <YoutubeInputWrapper>
                            <InputText label={"Link"} value={url} onChange={changeHref} />
                        </YoutubeInputWrapper>
                        <ArrowWrapper>
                            <Icon i="chevron-right" />
                        </ArrowWrapper>
                        <InputText
                            onChange={() => { }}
                            label={"ID"}
                            value={data.href || ""}
                            disabled
                        />
                    </LinkRow>
                    <TimeRow>
                        <Time>
                            <Label>
                                <FormattedMessage {...messages.start} />
                            </Label>
                            <Hour>
                                <InputNumber name="startHour" value={startHour?.toString() || ""} onChange={changeStartHour} />
                            </Hour>
                            <Dots>:</Dots>
                            <Minute>
                                <InputNumber name="startMinute" value={startMinute?.toString() || ""} onChange={changeStartMinute} />
                            </Minute>
                            <Dots>:</Dots>
                            <Second>
                                <InputNumber name="startSecond" value={startSecond?.toString() || ""} onChange={changeStartSecond} />
                            </Second>
                        </Time>
                        <Time>
                            <Label>
                                <FormattedMessage {...messages.end} />
                            </Label>
                            <Hour>
                                <InputNumber name="endHour" value={endHour?.toString() || ""} onChange={changeEndHour} />
                            </Hour>
                            <Dots>:</Dots>
                            <Minute>
                                <InputNumber name="endMinute" value={endMinute?.toString() || ""} onChange={changeEndMinute} />
                            </Minute>
                            <Dots>:</Dots>
                            <Second>
                                <InputNumber name="endSecond" value={endSecond?.toString() || ""} onChange={changeEndSecond} />
                            </Second>
                        </Time>
                    </TimeRow>
                </StyledInputs>
                {data.href && (
                    <IframeWrapper>
                        <div id={domId} />
                    </IframeWrapper>
                )}
            </RightSide>
        </Container>
    );
};

export default VideoEditor;
